import React from 'react';
import PageLayout from '../../components/layout/PageLayout';
import CaseStudyTemplate from '../../components/shared/CaseStudyTemplate';

const STREnterprisesPage = () => (
  <PageLayout>
    <CaseStudyTemplate
      seoTitle="STR Enterprises Case Study | Kalobrate"
      seoDescription="See how Kalobrate helped STR Enterprises understand employee experience, protect its family culture and identify practical priorities as the business grew."
      canonical="/results/str-enterprises"
      client="STR Enterprises"
      headline="Protecting what works as a family business grows"
      sector="Hospitality"
      typeOfWork="Culture review"
      intro={[
        'STR Enterprises had built a group of hospitality businesses around strong family values, close working relationships and genuine care for employees.',
        'As the business grew, its leaders wanted a clearer understanding of how people were experiencing the organisation across different sites and roles.',
        'The aim was not to add more wellbeing activity. It was to understand what was working well, what employees valued, what needed to be protected and where inconsistency or pressure could create future problems.',
      ]}
      sections={[
        {
          heading: 'The situation',
          content: [
            'The leadership team wanted more than a general employee survey or a list of standard recommendations.',
            'They needed to understand:',
            { type: 'list', items: [
              'Whether the family values were being experienced consistently',
              'Which parts of the culture employees wanted to keep',
              'Where expectations were unclear',
              'Where managers needed more structure or support',
              'Which improvements would be practical across different hotels and teams',
            ]},
            'Trust was central to the process. Employees needed to understand why information was being gathered, how it would be used and what they could reasonably expect to happen next.',
          ],
        },
        {
          heading: 'What Kalobrate did',
          content: [
            'Kalobrate carried out listening sessions across the business, giving employees space to speak openly about their experience of work.',
            'The process included:',
            { type: 'list', items: [
              'Confidential group discussions across business areas',
              'Conversations with owners and senior leaders',
              'Identification of common themes across sites and roles',
              'Comparison of employee experience with leadership intentions',
              'Practical recommendations based on the realities of the business',
            ]},
            'The focus was not on importing a generic workplace programme. It was on understanding what STR Enterprises already did well and where clearer systems or more consistent management would help.',
            'Kalobrate then worked with the leadership team to consider how the organisation\'s family values could be reflected more clearly in:',
            { type: 'list', items: [
              'Recruitment',
              'Onboarding',
              'Everyday management',
              'Team communication',
              'Employee expectations',
              'Future growth decisions',
            ]},
          ],
        },
        {
          heading: 'What the business received',
          content: [
            'STR Enterprises received:',
            { type: 'list', items: [
              'A clear summary of what employees valued',
              'An account of what should be protected as the business grew',
              'Identification of areas where the employee experience was less consistent',
              'Practical recommendations for leadership and management',
              'A clearer basis for deciding what to address first',
              'Ongoing confidential support when culture issues arise',
            ]},
            'The work gave leaders a clearer distinction between areas that needed new action and areas where the priority was to protect what was already working.',
          ],
        },
        {
          heading: 'Ongoing support',
          content: [
            'Kalobrate continues to support STR Enterprises when new workplace challenges arise.',
            'This gives business leaders a confidential space to think through culture issues, test possible responses and consider the likely effect on employees before taking action.',
            'The continuing relationship means culture is not treated as a one-off report or project. It remains part of the way leaders think about growth and change.',
          ],
        },
      ]}
      quotes={[
        { text: 'He took the time to listen and tailor feedback to the realities of our business operations and workforce.', name: 'Chris Sanderson', role: 'Group Managing Director' },
        { text: 'He created an environment where staff felt comfortable sharing openly.', name: 'Angela Thomas', role: 'Group Operations Manager' },
        { text: 'Recommendations were clear, relevant, achievable, and helped focus where it really matters.', name: 'Chris Sanderson', role: 'Group Managing Director' },
      ]}
      mainLesson={[
        'Growth does not only create new problems. It can also weaken the things that made the business successful in the first place.',
        'Workplace listening helped STR Enterprises identify both what needed to improve and what should not be lost.',
      ]}
      cta={{
        heading: 'Growing and concerned about what could be lost along the way?',
        copy: 'Kalobrate can help you understand what employees value, where pressure is building and what leadership should protect.',
        button: 'Talk to Ollie',
      }}
    />
  </PageLayout>
);

export default STREnterprisesPage;
